import { Box, Chip, Grid, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAppInfoTooltip from '../../../../../app/components/CAppInfoTooltip';
import CAppPaper from '../../../../../app/components/CAppPaper';
import AppFormatPercent from '../../../../../app/features/format/AppFormatPercent';
import AppFormatX from '../../../../../app/features/format/AppFormatX';
import IFundAsset from '../../../interfaces/fund/IFundAsset';
import investmentFundAssetsLang from '../../../langs/investmentFundAssetsLang';
import { Item } from './CFundInformationBlock';
import isoCountryCurrency from 'i18n-iso-countries';
import isoCountryCurrencyEN from 'i18n-iso-countries/langs/en.json';
import isoCountryCurrencyFR from 'i18n-iso-countries/langs/fr.json';
import AppFormatAmount from '../../../../../app/features/format/AppFormatAmount';
import TruncateMarkup from 'react-truncate-markup';

import langInvestWordLang from '../../../langs/langInvestWordLang';

isoCountryCurrency.registerLocale(isoCountryCurrencyFR);
isoCountryCurrency.registerLocale(isoCountryCurrencyEN);

type CInvestmentFundAssetProps = {
	investmentFundAsset: IFundAsset;
};

const CInvestmentFundAsset = (props: CInvestmentFundAssetProps) => {
	const { investmentFundAsset } = props;
	const theme = useTheme();
	const { t, i18n } = useTranslation();

	const [nbDescriptionLines, setNbDescriptionLines] = useState(5);

	// `rootEl` is the truncated `ReactNode` passed back by `react-truncate-markup`'s
	// `ellipsis` callback — its public type is `ReactNode`, but in practice the lib
	// passes a `ReactElement` whose inlined `children` is the truncated string.
	const readMoreEllipsis = (rootEl: React.ReactNode) => {
		const originalWordCount = investmentFundAsset?.descriptionText?.match(/\S+/g)?.length || 0;
		const newTruncatedText = (rootEl as React.ReactElement<{ children: string }>).props.children;
		const currentWordCount = newTruncatedText.match(/\S+/g)?.length ?? 0;

		return (
			<span>
				...{' '}
				<span
					onClick={() => setNbDescriptionLines(nbDescriptionLines + 100)}
					style={{ textDecoration: 'underline', cursor: 'pointer' }}
				>
					+{t(langInvestWordLang.x_words.tag, { count: originalWordCount - currentWordCount })}
				</span>
			</span>
		);
	};

	return (
		<CAppPaper variant="outlined" sx={{ padding: '0px', marginTop: '2rem' }}>
			<Grid container spacing={1}>
				<Grid item sm={3} xs={12}>
					<Box sx={{ padding: '30px' }}>
						{investmentFundAsset.imgSrc ? (
							<Box
								component="img"
								sx={{
									height: '52px',
									maxWidth: '100%',
									backgroundColor: 'white',
									padding: '10px',
									borderRadius: '10px',
									marginLeft: theme.palette.mode !== 'dark' ? '-10px' : '',
								}}
								src={investmentFundAsset.imgSrc}
							/>
						) : (
							<></>
						)}
						<Typography variant="h3" component="h3" sx={{ margin: '10px 0px', padding: '0px' }}>
							{investmentFundAsset.name}
						</Typography>
						<Box sx={{ fontSize: '0.82rem', margin: '10px 0px', fontWeight: 500 }}>
							{t(investmentFundAssetsLang.investment_at_title.tag)}{' '}
							{moment(investmentFundAsset?.investment?.date).format('DD/MM/YYYY')}
						</Box>
						<Box sx={{ fontSize: '0.82rem', margin: '10px 0px' }}>
							<TruncateMarkup lines={nbDescriptionLines} ellipsis={readMoreEllipsis}>
								<Box>{investmentFundAsset.descriptionText}</Box>
							</TruncateMarkup>
						</Box>
						<Box>
							{investmentFundAsset.currency ? (
								<Chip
									label={investmentFundAsset.currency.toUpperCase()}
									variant="outlined"
									color="primary"
									size="medium"
									sx={{
										margin: '5px 5px 5px 0px',
										color: theme.palette.mode === 'dark' ? theme.palette.primary.dark : '',
									}}
								/>
							) : (
								<></>
							)}
							{investmentFundAsset.countryCode ? (
								<Chip
									label={isoCountryCurrency.getName(investmentFundAsset.countryCode, i18n.language)}
									variant="outlined"
									color="primary"
									size="medium"
									sx={{
										margin: '5px 5px 5px 0px',
										color: theme.palette.mode === 'dark' ? theme.palette.primary.dark : '',
									}}
								/>
							) : (
								<></>
							)}
							{investmentFundAsset.sectorNameText ? (
								<Chip
									label={investmentFundAsset.sectorNameText}
									variant="outlined"
									color="primary"
									size="medium"
									sx={{
										margin: '5px 5px 5px 0px',
										color: theme.palette.mode === 'dark' ? theme.palette.primary.dark : '',
									}}
								/>
							) : (
								<></>
							)}
						</Box>
					</Box>
				</Grid>
				<Grid item sm={9} xs={12} sx={{ borderLeft: `2px solid ${theme.palette.background.default}` }}>
					<Box sx={{ padding: '30px' }}>
						<Typography
							variant="h2"
							component="h2"
							color="secondary"
							sx={{ margin: '0px', padding: '0px' }}
						>
							{t(investmentFundAssetsLang.multiple_title.tag)} :{' '}
							<AppFormatX
								value={investmentFundAsset?.investment?.multiple}
								textIfNull={t(langInvestWordLang.na.tag)}
							/>{' '}
							<CAppInfoTooltip title={t(investmentFundAssetsLang.multiple_tooltip.tag)} color="primary" />
						</Typography>

						<Typography variant="h3" component="h3" sx={{ margin: '20px 0px 0px 0px', padding: '0px' }}>
							{investmentFundAsset?.investment?.reportingPeriod
								? t(investmentFundAssetsLang.reporting_of_title.tag) +
								  ' Q' +
								  investmentFundAsset.investment.reportingPeriod.number +
								  ' ' +
								  investmentFundAsset.investment.reportingPeriod.year
								: t(investmentFundAssetsLang.no_quarterly_report_available.tag)}
						</Typography>
						<Grid container spacing={3}>
							<Grid item sm={4} xs={12}>
								<Item
									id="percent_of_asset_owned"
									label={t(investmentFundAssetsLang.percent_of_asset_owned.tag)}
									value={
										<AppFormatPercent
											value={investmentFundAsset?.investment?.percentOwned}
											textIfNull={t(langInvestWordLang.na.tag)}
										/>
									}
								/>
							</Grid>
							<Grid item sm={4} xs={12}>
								<Item
									id="contributed_capital"
									label={t(investmentFundAssetsLang.contributed_capital.tag)}
									value={
										<AppFormatAmount
											value={investmentFundAsset?.investment?.contributedCapital}
											addSpace
											currency={investmentFundAsset?.currency}
											textIfNull={t(langInvestWordLang.na.tag)}
										/>
									}
								/>
							</Grid>
						</Grid>
						<Grid container spacing={3}>
							<Grid item sm={4} xs={12}>
								<Item
									id="status"
									label={t(investmentFundAssetsLang.status.tag)}
									value={
										investmentFundAsset?.investment?.reportingPeriod ? (
											<>
												{investmentFundAsset.investment.reportingStatus === 'realized'
													? t(investmentFundAssetsLang.reporting_status_realized.tag)
													: investmentFundAsset.investment.reportingStatus === 'unrealized'
													? t(investmentFundAssetsLang.reporting_status_unrealized.tag)
													: investmentFundAsset.investment.reportingStatus === 'partial'
													? t(investmentFundAssetsLang.reporting_status_partial.tag)
													: ''}
											</>
										) : (
											<>{t(langInvestWordLang.na.tag)}</>
										)
									}
								/>
							</Grid>
							<Grid item sm={4} xs={12}>
								<Item
									id="fmv"
									label={t(investmentFundAssetsLang.fmv.tag)}
									value={
										<AppFormatAmount
											value={investmentFundAsset?.investment?.reportingFMV}
											addSpace
											currency={investmentFundAsset?.currency}
											textIfNull={t(langInvestWordLang.na.tag)}
										/>
									}
								/>
							</Grid>
						</Grid>
					</Box>
				</Grid>
			</Grid>
		</CAppPaper>
	);
};

export default CInvestmentFundAsset;
